import { useEffect, useState } from "react";
import api from "@/api";
import { Card } from "@/components/ui/card";
import { Users, Send, Eye, AlertTriangle, MailOpen, MousePointerClick, CalendarClock, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";

const FlagPill = ({ code, count }) => {
    const flags = { CH: "🇨🇭", DE: "🇩🇪", AT: "🇦🇹" };
    return (
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200">
            <span className="text-base">{flags[code]}</span>
            <span className="text-xs font-semibold text-slate-700">{code}</span>
            <span className="text-xs text-slate-400">·</span>
            <span className="text-xs font-bold text-[#1a2b48]">{count}</span>
        </div>
    );
};

const KpiCard = ({ icon: Icon, label, value, sub, accent = "blue", testid }) => {
    const accents = {
        blue: "from-[#007bff]/10 to-blue-50 text-[#007bff]",
        navy: "from-[#1a2b48]/10 to-slate-100 text-[#1a2b48]",
        green: "from-emerald-500/10 to-emerald-50 text-emerald-600",
        amber: "from-amber-500/10 to-amber-50 text-amber-600",
        rose: "from-rose-500/10 to-rose-50 text-rose-600",
    };
    return (
        <Card data-testid={testid} className="p-6 bg-white border-slate-200 shadow-sm rounded-xl group hover:-translate-y-0.5 hover:shadow-md transition-all duration-300 relative overflow-hidden">
            <div className={`absolute -top-10 -right-10 w-28 h-28 rounded-full bg-gradient-to-br ${accents[accent]} opacity-50 blur-2xl group-hover:opacity-70 transition-opacity`} />
            <div className="relative">
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${accents[accent]} flex items-center justify-center mb-4`}>
                    <Icon className="w-5 h-5" strokeWidth={1.7} />
                </div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-1">{label}</div>
                <div className="font-display text-4xl text-[#1a2b48] leading-none">{value}</div>
                {sub && <div className="text-xs text-slate-500 mt-2">{sub}</div>}
            </div>
        </Card>
    );
};

export default function DashboardPage() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/stats/dashboard").then((r) => setStats(r.data)).finally(() => setLoading(false));
    }, []);

    if (loading || !stats) {
        return <div className="text-slate-400 text-sm">Lade Dashboard ...</div>;
    }

    return (
        <div className="space-y-8 animate-fade-in-up" data-testid="dashboard-page">
            {/* Header */}
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.25em] text-[#007bff] font-bold mb-2">Übersicht</div>
                    <h1 className="font-display text-5xl text-[#1a2b48] leading-none">Dashboard.</h1>
                    <p className="text-slate-500 mt-3 max-w-xl">
                        Ihre Outreach-Kampagnen für Reisebüros in der DACH-Region auf einen Blick.
                    </p>
                </div>
                <Link
                    to="/campaigns"
                    data-testid="new-campaign-cta"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#1a2b48] hover:bg-[#2a3d60] text-white text-sm font-medium shadow-lg shadow-[#1a2b48]/10 transition-all hover:-translate-y-0.5"
                >
                    <Send className="w-4 h-4" /> Neue Kampagne
                </Link>
            </div>

            {/* KPI grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                <KpiCard testid="kpi-contacts" icon={Users} label="Reisebüros" value={stats.contacts.total}
                    sub={`${stats.contacts.active} aktiv`} accent="navy" />
                <KpiCard testid="kpi-sent" icon={Send} label="Gesendet" value={stats.emails.sent}
                    sub={`${stats.campaigns.total} Kampagnen`} accent="blue" />
                <KpiCard testid="kpi-opened" icon={MailOpen} label="Öffnungsrate"
                    value={`${stats.emails.open_rate}%`} sub={`${stats.emails.opened} geöffnet`} accent="green" />
                <KpiCard testid="kpi-bounced" icon={AlertTriangle} label="Bounce Rate"
                    value={`${stats.emails.bounce_rate}%`} sub={`${stats.emails.bounced} bounced`} accent="rose" />
            </div>

            {/* Country / Language distribution */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <Card className="p-6 bg-white border-slate-200 rounded-xl">
                    <div className="flex items-center justify-between mb-5">
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Verteilung</div>
                            <h3 className="text-lg font-semibold text-[#1a2b48] mt-0.5">Nach Ländern</h3>
                        </div>
                        <TrendingUp className="w-5 h-5 text-slate-300" />
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {Object.entries(stats.contacts.by_country).map(([k, v]) => (
                            <FlagPill key={k} code={k} count={v} />
                        ))}
                    </div>
                </Card>

                <Card className="p-6 bg-white border-slate-200 rounded-xl">
                    <div className="flex items-center justify-between mb-5">
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Verteilung</div>
                            <h3 className="text-lg font-semibold text-[#1a2b48] mt-0.5">Nach Sprache</h3>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {Object.entries(stats.contacts.by_language).map(([k, v]) => (
                            <div key={k} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100">
                                <span className="text-xs font-bold text-[#007bff]">{k}</span>
                                <span className="text-xs text-slate-400">·</span>
                                <span className="text-xs font-bold text-[#1a2b48]">{v}</span>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>

            {/* Recent campaigns */}
            <Card className="bg-white border-slate-200 rounded-xl overflow-hidden">
                <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Aktivität</div>
                        <h3 className="text-lg font-semibold text-[#1a2b48] mt-0.5">Letzte Kampagnen</h3>
                    </div>
                    <Link to="/campaigns" className="text-sm text-[#007bff] hover:text-[#0056b3] font-medium">Alle ansehen →</Link>
                </div>
                {stats.campaigns.recent.length === 0 ? (
                    <div className="p-12 text-center">
                        <CalendarClock className="w-10 h-10 text-slate-200 mx-auto mb-4" strokeWidth={1.5} />
                        <p className="text-slate-500 text-sm">Noch keine Kampagnen erstellt.</p>
                        <Link to="/campaigns" className="inline-block mt-4 text-sm text-[#007bff] font-medium hover:underline">
                            Erste Kampagne erstellen →
                        </Link>
                    </div>
                ) : (
                    <div className="divide-y divide-slate-100">
                        {stats.campaigns.recent.map((c) => (
                            <div key={c.id} className="p-5 flex items-center justify-between hover:bg-slate-50/50 transition-colors">
                                <div>
                                    <div className="font-medium text-[#1a2b48]">{c.name}</div>
                                    <div className="text-xs text-slate-500 mt-1">
                                        {c.stats.total} Empfänger · {c.stats.sent} gesendet · {c.stats.opened || 0} geöffnet
                                    </div>
                                </div>
                                <StatusBadge status={c.status} />
                            </div>
                        ))}
                    </div>
                )}
            </Card>
        </div>
    );
}

function StatusBadge({ status }) {
    const map = {
        draft: { label: "Entwurf", className: "bg-slate-100 text-slate-600" },
        scheduled: { label: "Geplant", className: "bg-amber-100 text-amber-700" },
        sending: { label: "Wird gesendet", className: "bg-blue-100 text-blue-700" },
        sent: { label: "Gesendet", className: "bg-emerald-100 text-emerald-700" },
        failed: { label: "Fehler", className: "bg-rose-100 text-rose-700" },
    };
    const m = map[status] || map.draft;
    return <Badge className={`${m.className} border-0 font-semibold`}>{m.label}</Badge>;
}
